"use client";

import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import gsap from "gsap";

export default function Section3Page() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const menuOverlayRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [timeStr, setTimeStr] = useState("( NYC )  12:59");
  const [coordsText, setCoordsText] = useState("40.7128° N, 74.0060° W");
  const menuTl = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    const updateTime = () => {
      const fmt = new Intl.DateTimeFormat([], {
        timeZone: "America/New_York",
        hour: "2-digit", minute: "2-digit", hour12: false
      });
      setTimeStr(`( NYC )  ${fmt.format(new Date())}`);
    };
    updateTime();
    const iv = setInterval(updateTime, 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (menuOpen) {
        const x = ((e.clientX / window.innerWidth) * 180 - 90).toFixed(4);
        const y = ((e.clientY / window.innerHeight) * 90 - 45).toFixed(4);
        const nx = Number(x), ny = Number(y);
        setCoordsText(`${Math.abs(nx)}° ${nx >= 0 ? "N" : "S"}, ${Math.abs(ny)}° ${ny >= 0 ? "E" : "W"}`);
      }
    };
    window.addEventListener("mousemove", handleMove);
    return () => window.removeEventListener("mousemove", handleMove);
  }, [menuOpen]);

  useEffect(() => {
    if (!canvasRef.current) return;

    let W = window.innerWidth, H = window.innerHeight;
    const canvas = canvasRef.current;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(W, H);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x060606);
    scene.fog = new THREE.FogExp2(0x060606, 0.018);

    const cam = new THREE.PerspectiveCamera(45, W / H, 0.1, 100);
    cam.position.set(-8.66, 3.93, -0.53);
    cam.lookAt(0, 3.6, 0);

    const clock = new THREE.Clock();

    // ── COMPOSER ──
    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, cam));

    const bloom = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      1.0,
      2.21,
      0.34
    );
    composer.addPass(bloom);

    const handleMouseBloom = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      bloom.strength = Math.abs(x);
    };
    window.addEventListener("mousemove", handleMouseBloom);

    // Vignette Pass
    const VignetteShader = {
      uniforms: {
        tDiffuse: { value: null },
        uStrength: { value: 0.75 },
        uBlur: { value: 0.035 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float uStrength;
        uniform float uBlur;
        varying vec2 vUv;
        void main() {
          vec2 uv = vUv;
          vec2 center = uv - 0.5;
          float dist = length(center);
          float vignette = smoothstep(0.3, 0.85, dist);
          vec2 blurDir = normalize(center) * vignette * 0.015;
          vec4 col = vec4(0.0);
          col += texture2D(tDiffuse, uv);
          col += texture2D(tDiffuse, uv - blurDir);
          col += texture2D(tDiffuse, uv + blurDir);
          col += texture2D(tDiffuse, uv - blurDir * 2.0);
          col += texture2D(tDiffuse, uv + blurDir * 2.0);
          col /= 5.0;
          col.rgb *= 1.0 - vignette * uStrength;
          gl_FragColor = col;
        }
      `
    };
    const vignettePass = new ShaderPass(VignetteShader);

    // Chromatic Pass
    const ChromaticShader = {
      uniforms: {
        tDiffuse: { value: null },
        uStrength: { value: 0.01 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float uStrength;
        varying vec2 vUv;
        void main() {
          vec2 uv = vUv;
          vec2 center = uv - 0.5;
          float dist = length(center);
          float strength = uStrength * dist;
          vec2 offset = normalize(center) * strength;
          vec3 col;
          col.r = texture2D(tDiffuse, uv + offset).r;
          col.g = texture2D(tDiffuse, uv).g;
          col.b = texture2D(tDiffuse, uv - offset).b;
          gl_FragColor = vec4(col,1.0);
        }
      `
    };
    const chromaticPass = new ShaderPass(ChromaticShader);

    composer.addPass(chromaticPass);
    composer.addPass(vignettePass);

    // ── LIGHTS ──
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.02);
    scene.add(ambientLight);

    const spotLight1 = new THREE.SpotLight(0xffa235, 12.0);
    spotLight1.position.set(-3, 8, -2);
    spotLight1.angle = Math.PI / 4.5;
    spotLight1.penumbra = 0.8;
    spotLight1.decay = 1.2;
    scene.add(spotLight1);

    const spotLight2 = new THREE.SpotLight(0xffd275, 10.0);
    spotLight2.position.set(4, 9, 2);
    spotLight2.angle = Math.PI / 4.5;
    spotLight2.penumbra = 0.8;
    spotLight2.decay = 1.2;
    scene.add(spotLight2);

    const target1 = new THREE.Object3D();
    target1.position.set(-1.2, 0.6, 1);
    scene.add(target1);
    spotLight1.target = target1;

    const target2 = new THREE.Object3D();
    target2.position.set(1.5, 0.6, 0);
    scene.add(target2);
    spotLight2.target = target2;

    // ── LINE MATERIAL ──
    class LineMaterial extends THREE.MeshStandardMaterial {
      params: any;
      uniforms: any;

      constructor(options = {}) {
        super(options);

        this.params = {
          scale: 0.7,
          inkColor: new THREE.Color(0x9E9E9E),
          backgroundColor: new THREE.Color(0x0F0F0F),
          angleStep: 1.8,
          angle: 0.3,
          thickness: 3.2,
          min: 0.09,
          max: 1.61,
          rim: 1.9,
          rimColor: new THREE.Color(0x0087db),
          noiseScale: 0.01,
          noiseAmplitude: 0.1,
          linesNoiseScale: 0.7,
          linesNoiseAmplitude: 1.1,
        };

        this.uniforms = {
          resolution: { value: new THREE.Vector2(W, H) },
          uTime: { value: 0 },
          angleStep: { value: this.params.angleStep },
          scale: { value: this.params.scale },
          angle: { value: this.params.angle },
          thickness: { value: this.params.thickness },
          inkColor: { value: this.params.inkColor },
          backgroundColor: { value: this.params.backgroundColor },
          range: { value: new THREE.Vector2(this.params.min, this.params.max) },
          rim: { value: this.params.rim },
          rimColor: { value: this.params.rimColor },
          noiseScale: { value: this.params.noiseScale },
          noiseAmplitude: { value: this.params.noiseAmplitude },
          linesNoiseScale: { value: this.params.linesNoiseScale },
          linesNoiseAmplitude: { value: this.params.linesNoiseAmplitude },
        };

        this.onBeforeCompile = (shader) => {
          for (const uniformName of Object.keys(this.uniforms)) {
            shader.uniforms[uniformName] = this.uniforms[uniformName];
          }

          shader.vertexShader = shader.vertexShader.replace(
            `#include <common>`,
            `#include <common>
            uniform float uTime;
            out vec2 vCoords;
            out vec3 vCameraNormal;
            out vec4 vWorldPosition;
            out vec3 vPosition;

            vec3 hash3(vec3 p) {
              p = vec3(dot(p, vec3(127.1, 311.7, 74.7)),
                       dot(p, vec3(269.5, 183.3, 246.1)),
                       dot(p, vec3(113.5, 271.9, 124.6)));
              return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
            }
            float noise3(vec3 p) {
              vec3 i = floor(p);
              vec3 f = fract(p);
              vec3 u = f * f * (3.0 - 2.0 * f);
              return mix(mix(mix(dot(hash3(i + vec3(0,0,0)), f - vec3(0,0,0)),
                                 dot(hash3(i + vec3(1,0,0)), f - vec3(1,0,0)), u.x),
                             mix(dot(hash3(i + vec3(0,1,0)), f - vec3(0,1,0)),
                                 dot(hash3(i + vec3(1,1,0)), f - vec3(1,1,0)), u.x), u.y),
                         mix(mix(dot(hash3(i + vec3(0,0,1)), f - vec3(0,0,1)),
                                 dot(hash3(i + vec3(1,0,1)), f - vec3(1,0,1)), u.x),
                             mix(dot(hash3(i + vec3(0,1,1)), f - vec3(0,1,1)),
                                 dot(hash3(i + vec3(1,1,1)), f - vec3(1,1,1)), u.x), u.y), u.z);
            }
            `
          );

          shader.vertexShader = shader.vertexShader.replace(
            `#include <begin_vertex>`,
            `#include <begin_vertex>
            float stepTime = floor(uTime * 12.0) * 0.083;
            float disp = noise3(position * 8.0 + stepTime) * 0.015;
            transformed += normal * disp;
            `
          );

          shader.vertexShader = shader.vertexShader.replace(
            `#include <uv_vertex>`,
            `#include <uv_vertex>
            vCoords = uv;
            vCameraNormal = normalMatrix * normal;
            vWorldPosition = modelViewMatrix * vec4(position, 1.);
            `
          );

          shader.fragmentShader = shader.fragmentShader.replace(
            `#include <common>`,
            `#include <common>
            uniform vec2 resolution;
            uniform float uTime;
            uniform float scale;
            uniform vec3 inkColor;
            uniform vec3 backgroundColor;
            uniform float thickness;
            uniform vec2 range;
            uniform float angleStep;
            uniform float angle;
            uniform float rim;
            uniform vec3 rimColor;
            uniform float noiseScale;
            uniform float noiseAmplitude;
            uniform float linesNoiseScale;
            uniform float linesNoiseAmplitude;
            in vec2 vCoords;
            in vec3 vCameraNormal;
            in vec4 vWorldPosition;

            #define PI 3.141592653589793

            vec2 hash( vec2 p ) {
              p = vec2( dot(p,vec2(127.1,311.7)),
                  dot(p,vec2(269.5,183.3)) );
              return -1.0 + 2.0*fract(sin(p)*43758.5453123);
            }

            float noise( in vec2 p ) {
              const float K1 = 0.366025404;
              const float K2 = 0.211324865;
              vec2 i = floor( p + (p.x+p.y)*K1 );
              vec2 a = p - i + (i.x+i.y)*K2;
              vec2 o = (a.x>a.y) ? vec2(1.0,0.0) : vec2(0.0,1.0);
              vec2 b = a - o + K2;
              vec2 c = a - 1.0 + 2.0*K2;
              vec3 h = max( 0.5-vec3(dot(a,a), dot(b,b), dot(c,c) ), 0.0 );
              vec3 n = h*h*h*h*vec3( dot(a,hash(i+0.0)), dot(b,hash(i+o)), dot(c,hash(i+1.0)));
              return dot( n, vec3(70.0) );
            }

            float luma(vec3 color) {
              return dot(color, vec3(0.299, 0.587, 0.114));
            }

            float lines( in float l, in vec2 fragCoord, in vec2 resolution, in float thickness, in float e){
              vec2 uv = fragCoord.xy * resolution;
              float c = (.5 + .5 * sin(uv.x*.5));
              float f = (c+thickness)*l;
              f = smoothstep(.5-e, .5+e, f);
              return f;
            }
            `
          );

          shader.fragmentShader = shader.fragmentShader.replace(
            "#include <dithering_fragment>",
            `#include <dithering_fragment>
            float l = luma(gl_FragColor.rgb);
            l = range.x + l * (range.y - range.x);
            vec3 n = normalize(vCameraNormal);
            float a = atan(n.y, n.x);
            a = round(a*angleStep)/angleStep;
            float r = max( 0., abs( dot( normalize( vNormal ), normalize( -vWorldPosition.xyz ) ) ) );
            float de = .001 * length(vec2(dFdx(gl_FragCoord.x), dFdy(gl_FragCoord.y)));
            float e = .1 * de;
            float rimMask = 1.0 - r;
            rimMask = smoothstep(.6, 1.0, rimMask);
            float stepTime = floor(uTime * 10.0) * 0.100;
            float outlineJitter = noise(gl_FragCoord.xy * 0.05 + stepTime) * 0.05;
            float border = pow(smoothstep(0., 0.25 + outlineJitter, r), rim);
            l *= border;
            r = smoothstep(.8 + outlineJitter, .8 + outlineJitter + e, r);
            a *= 1.-r;
            a += PI / 2.;
            a += angle;
            float s = sin(a);
            float c = cos(a);
            mat2 rot = mat2(c, -s, s, c);
            vec2 coords = scale*100.*(vWorldPosition.xy/(de*500.)) + linesNoiseAmplitude * noise(linesNoiseScale*vWorldPosition.xy + stepTime);
            coords = rot * coords;
            float line = lines(l, coords, vec2(5.), thickness + noiseAmplitude * noise(noiseScale *coords.xy + stepTime), e);
            vec3 base = mix(backgroundColor, inkColor, 1.0 - line);
            base = mix(base, rimColor, rimMask);
            gl_FragColor.rgb = base;
            `
          );
        };
      }
    }

    const customMaterial = new LineMaterial({
      color: 0xcccccc,
      roughness: 0.5,
      metalness: 0.1
    });

    // Floor
    const floorGeo = new THREE.PlaneGeometry(80, 80);
    const floorMat = new THREE.MeshStandardMaterial({ color: 0x030303, roughness: 0.18, metalness: 0.90 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    scene.add(floor);

    // DRACO + GLTF environment load
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.7/");
    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    loader.load(
      "/models/assets/environment.glb",
      (gltf) => {
        const model = gltf.scene;
        model.scale.set(1.2, 1.2, 1.2);
        model.position.set(0, 0, 0);

        model.traverse((node: any) => {
          if (node.isMesh) {
            if (node.name.toLowerCase().includes("screen") || node.name.toLowerCase().includes("display")) {
              if (node.material && node.material.map) {
                node.material = new THREE.MeshBasicMaterial({ map: node.material.map, side: THREE.DoubleSide });
              }
            } else {
              node.material = customMaterial;
            }
          }
        });
        scene.add(model);

        gsap.to(overlayRef.current, {
          opacity: 0, duration: 1.2, ease: "power3.out",
          onComplete: () => { if (overlayRef.current) overlayRef.current.style.pointerEvents = "none"; }
        });
      }
    );

    // Navigation
    let leaving = false;
    history.pushState({ page: 1 }, "", "");

    const navigateTo = (page: string) => {
      if (leaving) return;
      leaving = true;
      const overlay = overlayRef.current;
      if (overlay) { overlay.style.opacity = "1"; overlay.style.pointerEvents = "all"; }
      const clean = page.replace(".html", "").replace("./", "");
      const routeMap: Record<string, string> = {
        "section2": "/portofolio/model/path/section2",
        "section3": "/portofolio/model/path/section3",
        "desert": "/portofolio/model/path/desert",
        "page": "/portofolio/model/path/page",
        "section": "/portofolio/model/path/section",
      };
      setTimeout(() => {
        if (clean.startsWith("/")) { window.location.href = clean; return; }
        window.location.href = routeMap[clean] || `/portofolio/model/path/${clean}`;
      }, 800);
    };

    (window as any).navigateTo = navigateTo;

    // Render loop
    let reqId: number;
    const render = () => {
      reqId = requestAnimationFrame(render);
      const t = clock.getElapsedTime();
      if (customMaterial.uniforms && customMaterial.uniforms.uTime) {
        customMaterial.uniforms.uTime.value = t;
      }
      composer.render();
    };
    render();

    const handleResize = () => {
      W = window.innerWidth; H = window.innerHeight;
      renderer.setSize(W, H);
      cam.aspect = W / H;
      cam.updateProjectionMatrix();
      if (customMaterial.uniforms && customMaterial.uniforms.resolution) {
        customMaterial.uniforms.resolution.value.set(W, H);
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(reqId);
      window.removeEventListener("mousemove", handleMouseBloom);
      window.removeEventListener("resize", handleResize);
      renderer.dispose();
    };
  }, []);

  const openMenuFn = () => {
    setMenuOpen(true);
    if (menuTl.current) menuTl.current.kill();
    const tl = gsap.timeline();
    menuTl.current = tl;
    tl.to(menuOverlayRef.current, { clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)", duration: 1.1, ease: "power4.inOut" });
    tl.fromTo("#menu-overlay .char-inner", { y: "105%", rotateX: -15 }, { y: "0%", rotateX: 0, duration: 1.0, ease: "power3.out", stagger: 0.02 }, "-=0.6");
    tl.fromTo("#menu-overlay .menu-meta-item", { opacity: 0, y: 15 }, { opacity: 1, y: 0, duration: 0.8, ease: "power2.out", stagger: 0.08 }, "-=0.7");
    tl.fromTo(closeBtnRef.current, { opacity: 0 }, { opacity: 0.6, duration: 0.5 }, "-=0.8");
  };

  const closeMenuFn = () => {
    if (menuTl.current) menuTl.current.kill();
    const tl = gsap.timeline({ onComplete: () => setMenuOpen(false) });
    menuTl.current = tl;
    tl.to("#menu-overlay .char-inner", { y: "-105%", duration: 0.6, ease: "power3.in", stagger: 0.01 });
    tl.to("#menu-overlay .menu-meta-item", { opacity: 0, y: -10, duration: 0.5, stagger: 0.05 }, 0);
    tl.to(closeBtnRef.current, { opacity: 0, duration: 0.3 }, 0);
    tl.to(menuOverlayRef.current, {
      clipPath: "polygon(0 100%, 100% 100%, 100% 100%, 0 100%)", duration: 0.9, ease: "power4.inOut",
      onComplete: () => { gsap.set(menuOverlayRef.current, { clipPath: "polygon(0 0, 100% 0, 100% 0, 0 0)" }); }
    }, "-=0.5");
  };

  const navTo = (page: string) => {
    if ((window as any).navigateTo) {
      (window as any).navigateTo(page);
    }
  };

  return (
    <div style={{ width: "100vw", height: "100vh", overflow: "hidden", background: "#000", color: "#fff" }}>
      <style>{`
        *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
        html,body{width:100%;height:100%;overflow:hidden;background:#000;color:#fff;font-family:'Inter',sans-serif;}
        #c{position:fixed;inset:0;width:100%;height:100%;display:block;z-index:1;}
        #ui{position:fixed;inset:0;pointer-events:none;z-index:10;}
        .nav{position:absolute;top:0;left:0;right:0;padding:22px 34px;display:flex;justify-content:space-between;align-items:center;pointer-events:auto;}
        .nav-logo{font-size:11px;letter-spacing:.13em;font-weight:700;cursor:pointer;}
        .nav-center{font-size:10px;letter-spacing:.10em;opacity:.65;}
        .nav-right{display:flex;align-items:center;gap:14px;font-size:11px;letter-spacing:.12em;font-weight:700;cursor:pointer;}
        .sound-bars{display:flex;align-items:flex-end;gap:2px;height:14px;}
        .sound-bar{width:2px;background:#fff;border-radius:1px;animation:sb .85s ease-in-out infinite alternate;}
        .sound-bar:nth-child(1){height:30%;animation-delay:0s;}
        .sound-bar:nth-child(2){height:100%;animation-delay:.2s;}
        .sound-bar:nth-child(3){height:55%;animation-delay:.1s;}
        .sound-bar:nth-child(4){height:75%;animation-delay:.3s;}
        @keyframes sb{0%{transform:scaleY(.25);}100%{transform:scaleY(1);}}
        .bottom-title{position:absolute;bottom:40px;left:50%;transform:translateX(-50%);text-align:center;pointer-events:auto;z-index:12;}
        .bottom-title h1{font-family:'Syncopate',sans-serif;font-size:clamp(24px,4.5vw,52px);font-weight:700;text-transform:uppercase;line-height:0.95;letter-spacing:-0.02em;margin:0;color:#fff;text-shadow:0 0 20px rgba(255,255,255,0.1);}
        .bottom-title h1 span.green-accent{color:#3bcf63;text-shadow:0 0 15px rgba(59,207,99,0.4);}
        #overlay{position:fixed;inset:0;background:#000;opacity:1;pointer-events:all;transition:opacity .8s cubic-bezier(0.25,1,0.5,1);z-index:1000;}
        .menu-overlay{position:fixed;inset:0;background:rgba(3,3,3,0.97);backdrop-filter:blur(25px);-webkit-backdrop-filter:blur(25px);z-index:2000;clip-path:polygon(0 0,100% 0,100% 0,0 0);display:flex;flex-direction:column;justify-content:center;padding:60px 10%;pointer-events:none;}
        .menu-overlay.active{pointer-events:auto;}
        .menu-close{position:absolute;top:40px;right:40px;display:flex;align-items:center;gap:12px;font-size:11px;letter-spacing:.15em;font-weight:700;cursor:pointer;opacity:.6;transition:opacity .3s,transform .3s;}
        .menu-close:hover{opacity:1;transform:scale(1.05);}
        .close-icon{position:relative;width:16px;height:16px;}
        .close-icon .line{position:absolute;top:8px;left:0;width:100%;height:1.5px;background:#fff;transition:transform .3s;}
        .close-icon .line-1{transform:rotate(45deg);}
        .close-icon .line-2{transform:rotate(-45deg);}
        .menu-container{display:grid;grid-template-columns:4.5fr 7.5fr;gap:80px;align-items:center;width:100%;max-width:1200px;margin:0 auto;}
        .menu-left{display:flex;flex-direction:column;gap:36px;border-right:1px solid rgba(255,255,255,0.08);padding-right:60px;}
        .menu-meta-item{display:flex;flex-direction:column;gap:8px;}
        .meta-label{font-size:9px;font-weight:700;letter-spacing:.18em;color:rgba(255,255,255,0.4);text-transform:uppercase;}
        .meta-value{font-size:11px;letter-spacing:.08em;line-height:1.6;color:rgba(255,255,255,0.7);}
        .meta-value.current{color:#fff;font-weight:600;}
        .menu-nav{display:flex;flex-direction:column;gap:24px;}
        .menu-item-wrap{overflow:hidden;display:block;}
        .menu-link{font-family:'Syncopate',sans-serif;font-size:clamp(28px,4.2vw,56px);font-weight:700;letter-spacing:.04em;text-decoration:none;color:rgba(255,255,255,0.25);display:inline-flex;align-items:center;gap:20px;transition:color 0.4s cubic-bezier(0.25, 1, 0.5, 1);position:relative;}
        .menu-link::before{content:attr(data-number);font-family:'Inter',sans-serif;font-size:clamp(10px,1.2vw,14px);font-weight:700;letter-spacing:.1em;color:rgba(255,255,255,0.15);margin-top:6px;align-self:flex-start;transition:color 0.4s;}
        .menu-link:hover{color:#fff;}
        .menu-link:hover::before{color:rgba(255,255,255,0.6);}
        .char-wrap{display:inline-block;overflow:hidden;vertical-align:bottom;}
        .char-inner{display:inline-block;transform:translateY(105%);transition:transform 0.5s cubic-bezier(0.215,0.61,0.355,1);}
        .menu-nav:hover .menu-link:not(:hover){opacity:0.35;}
        @media(max-width:768px){
          .nav{padding:14px 18px;}
          .nav-center{display:none;}
          .menu-overlay{padding:72px 6% 32px;justify-content:flex-start;overflow-y:auto;}
          .menu-close{top:20px;right:18px;font-size:10px;}
          .menu-container{grid-template-columns:1fr;gap:0;}
          .menu-left{display:none;}
          .menu-nav{gap:10px;}
          .menu-link{font-size:clamp(15px,5.5vw,24px);}
          .menu-link::before{font-size:9px;}
        }
        @media(max-width:480px){
          .menu-overlay{padding-top:60px;}
          .menu-link{font-size:clamp(14px,6vw,20px);}
          .bottom-title{bottom:20px;}
          .bottom-title h1{font-size:clamp(20px,7vw,32px);}
        }
      `}</style>

      <canvas ref={canvasRef} id="c" />

      <div ref={overlayRef} id="overlay" />

      {/* MENU OVERLAY */}
      <div ref={menuOverlayRef} id="menu-overlay" className={`menu-overlay${menuOpen ? " active" : ""}`}>
        <div ref={closeBtnRef} className="menu-close" id="menu-close" onClick={closeMenuFn}>
          <span>CLOSE</span>
          <div className="close-icon">
            <div className="line line-1" />
            <div className="line line-2" />
          </div>
        </div>
        <div className="menu-container">
          <div className="menu-left">
            <div className="menu-meta-item">
              <div className="meta-label">SECTIONS</div>
              <div className="meta-value">01 / CREATOR TECHNOLOGY</div>
              <div className="meta-value current">02 / CREATOR COMMUNITIES (CURRENT)</div>
              <div className="meta-value">03 / CREATOR MEDIA</div>
              <div className="meta-value">04 / CREATOR PRODUCTS</div>
            </div>
            <div className="menu-meta-item">
              <div className="meta-label">COORDINATES &amp; TIME</div>
              <div className="meta-value" id="menu-coords">{coordsText}</div>
              <div className="meta-value" id="menu-time">{timeStr}</div>
            </div>
            <div className="menu-meta-item">
              <div className="meta-label">MISSION</div>
              <div className="meta-value">BUILDING THE OPERATING SYSTEM FOR THE CREATOR ECONOMY. EMPOWERING COMMUNITIES AND MEDIA NARRATIVES GLOBALLY.</div>
            </div>
          </div>
          <nav className="menu-nav">
            <div className="menu-item-wrap">
              <a href="/portofolio/model/path/section2" className="menu-link" data-number="01" onClick={(e) => { e.preventDefault(); closeMenuFn(); setTimeout(() => navTo("/portofolio/model/path/section2"), 500); }}>
                <span className="menu-link-text">TECHNOLOGY</span>
              </a>
            </div>
            <div className="menu-item-wrap">
              <a href="/portofolio/model/path/section3" className="menu-link active" data-number="02" onClick={(e) => { e.preventDefault(); closeMenuFn(); setTimeout(() => navTo("/portofolio/model/path/section3"), 500); }}>
                <span className="menu-link-text">COMMUNITIES</span>
              </a>
            </div>
            <div className="menu-item-wrap">
              <a href="/portofolio/model/path/desert" className="menu-link" data-number="03" onClick={(e) => { e.preventDefault(); closeMenuFn(); setTimeout(() => navTo("/portofolio/model/path/desert"), 500); }}>
                <span className="menu-link-text">MEDIA</span>
              </a>
            </div>
            <div className="menu-item-wrap">
              <a href="/portofolio/model/path/page" className="menu-link" data-number="04" onClick={(e) => { e.preventDefault(); closeMenuFn(); setTimeout(() => navTo("/portofolio/model/path/page"), 500); }}>
                <span className="menu-link-text">PRODUCTS</span>
              </a>
            </div>
          </nav>
        </div>
      </div>

      <div id="ui">
        {/* NAV */}
        <nav className="nav">
          <div className="nav-logo" onClick={() => navTo("/portofolio/model/path/section2")}>HUCO&nbsp;•&nbsp;</div>
          <div className="nav-center" id="nav-time">{timeStr}</div>
          <div className="nav-right" onClick={openMenuFn}>
            <div className="sound-bars">
              <div className="sound-bar" />
              <div className="sound-bar" />
              <div className="sound-bar" />
              <div className="sound-bar" />
            </div>
            MENU
          </div>
        </nav>

        {/* BOTTOM HEADER */}
        <div className="bottom-title">
          <h1>CREATOR<br /><span className="green-accent">COMMUNITIES</span></h1>
        </div>
      </div>
    </div>
  );
}
